import { can, type CanOptions } from '../permissionLogic';
import type {
  NormalizedResourcePermission,
  PermissionAction,
  PermissionContext,
} from '../types';
import {
  SCHEDULER_NAV_PERMISSIONS,
  SCHEDULER_RESOURCES,
  type SchedulerNavPermission,
} from './resources';

/**
 * Check action on a leaf resource, or parent hub / module as fallback.
 * Lets a grant on `scheduler` (or a hub) cover nested resources until finer grants exist.
 */
export function canSchedulerResource(
  index: Map<string, NormalizedResourcePermission>,
  resourceId: string,
  action: PermissionAction,
  context: PermissionContext,
  options: CanOptions = {}
): boolean {
  if (can(index, resourceId, action, context, options)) {
    return true;
  }

  if (
    resourceId.startsWith(`${SCHEDULER_RESOURCES.dashboard}.`) &&
    can(index, SCHEDULER_RESOURCES.dashboard, action, context, options)
  ) {
    return true;
  }

  if (
    resourceId.startsWith(`${SCHEDULER_RESOURCES.schedule}.`) &&
    can(index, SCHEDULER_RESOURCES.schedule, action, context, options)
  ) {
    return true;
  }

  if (
    resourceId !== SCHEDULER_RESOURCES.module &&
    resourceId.startsWith(`${SCHEDULER_RESOURCES.module}.`) &&
    can(index, SCHEDULER_RESOURCES.module, action, context, options)
  ) {
    return true;
  }

  return false;
}

/** @deprecated Prefer canSchedulerResource(..., 'view', ...) */
export function canViewSchedulerResource(
  index: Map<string, NormalizedResourcePermission>,
  resourceId: string,
  context: PermissionContext,
  options: CanOptions = {}
): boolean {
  return canSchedulerResource(index, resourceId, 'view', context, options);
}

export function canShowSchedulerNav(
  index: Map<string, NormalizedResourcePermission>,
  nav: SchedulerNavPermission,
  context: PermissionContext,
  options: CanOptions = {}
): boolean {
  return canSchedulerResource(index, nav.resourceId, nav.action, context, options);
}

/** True if the user can view at least one Scheduler destination */
export function hasAnySchedulerViewAccess(
  index: Map<string, NormalizedResourcePermission>,
  context: PermissionContext,
  options: CanOptions & { ready?: boolean } = {}
): boolean {
  const { ready = true, failClosed = true } = options;

  if (!ready) {
    return !failClosed;
  }

  if (can(index, SCHEDULER_RESOURCES.module, 'view', context, { ready: true })) {
    return true;
  }

  for (const nav of Object.values(SCHEDULER_NAV_PERMISSIONS)) {
    if (canShowSchedulerNav(index, nav, context, { ready: true })) {
      return true;
    }
  }

  return false;
}
